import { initializeSessionReplay } from '@edulastic/api/src/amplitudeSessionReplay'
import { initSegmentDebugger } from '@edulastic/api/src/segmentDebugger'
import AppConfig from '../../../app-config'

// Flag to prevent duplicate Session Replay initialization
let isSessionReplayInitialized = false

export const initializeSegment = async (forceEnable = false) => {
  if (!(AppConfig.isSegmentEnabled || forceEnable)) return

  // Snippet is refactored version of
  // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/
  window.analytics = window.analytics || []
  const { analytics } = window
  if (analytics.initialize) return
  if (analytics.invoked) {
    if (window.console && console.error)
      console.error('Segment snippet included twice.')
    return
  }

  analytics.invoked = true
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ]
  analytics.factory = function (method) {
    return function () {
      // eslint-disable-next-line prefer-rest-params
      const args = Array.prototype.slice.call(arguments)
      args.unshift(method)
      analytics.push(args)
      return analytics
    }
  }
  for (let i = 0; i < analytics.methods.length; i++) {
    const key = analytics.methods[i]
    analytics[key] = analytics.factory(key)
  }
  analytics.load = function (key, options) {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    // download the file from by passing the key https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js
    // and upload it to s3
    script.src = AppConfig.segmentURI
    const first = document.getElementsByTagName('script')[0]
    first.parentNode.insertBefore(script, first)
    analytics._loadOptions = options
  }
  analytics.SNIPPET_VERSION = AppConfig.segmentVersion
  analytics.load()

  // NOTE: Session Replay is NOT initialized here anymore
  // It's initialized after login in fetchUser saga with the user's role
  // This ensures students never have Session Replay initialized (saves Amplitude resources)
}

/**
 * Initialize Session Replay after user login
 * This should be called from the fetchUser saga after we know the user's role
 *
 * @param {string} userRole - The role of the logged-in user (e.g., 'teacher', 'student')
 * @returns {Promise<void>}
 */
export const initializeSessionReplayAfterLogin = async (userRole) => {
  // Prevent duplicate initialization
  if (isSessionReplayInitialized) {
    if (AppConfig.isSessionReplayDebugModeEnabled) {
      console.log('[Session Replay] ⏭️ Already initialized, skipping...')
    }
    return
  }

  // Check if Session Replay is enabled
  if (!AppConfig.isSessionReplayEnabled || !AppConfig.amplitudeApiKey) {
    return
  }

  // Check if Segment analytics is ready
  const { analytics } = window
  if (!analytics || !analytics.ready) {
    console.warn('[Session Replay] ⚠️ Segment analytics not available')
    return
  }

  const isDebugMode = AppConfig.isSessionReplayDebugModeEnabled

  // Wait for Segment to be ready before initializing Session Replay
  // Add timeout to prevent hanging promises in background (login already non-blocking with fork)
  // This handles cases where Segment is blocked by ad blockers, network issues, etc.
  return new Promise((resolve) => {
    const TIMEOUT_MS = 10000 // 10 second timeout
    let resolved = false

    // Timeout handler - prevents hanging promises in background if Segment is blocked or slow
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true
        console.warn(
          '[Session Replay] ⚠️ Segment analytics.ready() timeout (Segment may be blocked) - skipping initialization'
        )
        resolve() // Resolve to prevent hanging promise
      }
    }, TIMEOUT_MS)

    try {
      analytics.ready(async () => {
        if (resolved) return // Already timed out

        clearTimeout(timeoutId)
        resolved = true

        try {
          if (isDebugMode) {
            console.log(
              `[Session Replay] 📡 Initializing for ${userRole} user...`
            )
          }

          // Initialize Session Replay SDK with user role
          await initializeSessionReplay(
            AppConfig.amplitudeApiKey,
            analytics,
            AppConfig.amplitudeSampleRate,
            isDebugMode,
            userRole
          )

          isSessionReplayInitialized = true

          // Initialize debugger
          if (isDebugMode) {
            initSegmentDebugger(analytics, true)
            console.log('[Session Replay] ✅ Ready')
          }

          resolve()
        } catch (error) {
          console.error('[Session Replay] ❌ Init failed:', error)
          // Resolve to prevent hanging promise (login already non-blocking with fork)
          resolve()
        }
      })
    } catch (error) {
      // If analytics.ready() throws synchronously, catch it
      clearTimeout(timeoutId)
      if (!resolved) {
        resolved = true
        console.error(
          '[Session Replay] ❌ Error setting up analytics.ready():',
          error
        )
        resolve() // Resolve to prevent hanging promise
      }
    }
  })
}

/**
 * Reset the Session Replay initialized flag
 * Call this on logout to allow re-initialization for the next user
 */
export const resetSessionReplayInitFlag = () => {
  isSessionReplayInitialized = false
}

/**
 * Searches `arr` for first match in `queries`.
 * If match is not found for ith query, next query is tried.
 * If no match is found at all, returns the `_default`
 * Example 1:
 * ```js
 * multiFind(
 *   [{ a: 0 }, { b: 1, bb: 11 }, { c: 2 }],
 *   [{ a: 2 }, { b: 1 }]
 * )
 * // returns {b: 1, bb: 11}
 * ```
 * Example 2:
 * ```js
 * multiFind(
 *   [{ a: 0 }, { b: 1, bb: 11 }, { c: 2 }],
 *   [{ a: 2 }, { b: 2 }, (v) => v.b == 1]
 * )
 * // returns {b: 1, bb: 11}
 * ```
 * Example 3:
 * ```js
 * multiFind(
 *   [{ a: 0 }, { b: 1, bb: 11 }, { c: 2 }],
 *   [{ a: 2 }, { b: 2 }, (v) => v.b == 3],
 *   {a: 'default'}
 * )
 * // returns {a: 'default'}
 * ```
 * @template arrayElement, defaultType
 * @param {arrayElement[]} arr Array to search into
 * @param {(Function|object)[]} queries array of queries. Query may be function or object
 * @param {defaultType} _default default value to return if no match found
 * @returns {arrayElement | defaultType}
 */
export function multiFind(arr, queries, _default = undefined) {
  for (const query of queries) {
    const findFunc =
      typeof query === 'function'
        ? query
        : (val) => {
            return Object.entries(query).every(([k, v]) => val[k] === v)
          }
    const foundIndex = arr.findIndex(findFunc)
    if (foundIndex !== -1) return arr[foundIndex]
  }
  return _default
}
